import {
    afterRenderEffect,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    forwardRef,
    inject,
    input,
    InputSignal,
    Signal,
    signal,
    untracked,
    viewChild,
    viewChildren,
    WritableSignal
} from '@angular/core';
import {SelectOption} from '../../../models/forms/select-option.model';
import {AuthImagePipe} from '../../../pipes/auth-image.pipe';
import {IconComponent} from '../../icon/icon.component';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {InputService} from '../../../services/input/input.service';
import {ICONS} from '../../../constants/icons/icons.constants';

/*
 * This component only supports Reactive Forms (ControlValueAccessor). As soon as it is used
 * with Signal Forms ([formField]), it has to be migrated to FormValueControl (see TextInputComponent).
 */
@Component({
    selector: 'app-select',
    imports: [
        AuthImagePipe,
        IconComponent,
    ],
    templateUrl: './select.component.html',
    styleUrl: './select.component.css',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectComponent),
            multi: true
        }
    ],
    host: {
        '(focusout)': 'onHostElementFocusOut($event)',
        '(document:pointerdown)': 'onDocumentPointerDown($event)'
    },
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectComponent implements ControlValueAccessor {
    public options: InputSignal<Array<SelectOption>> = input.required<Array<SelectOption>>();
    public name: InputSignal<string> = input.required<string>();
    public qaTag: InputSignal<string> = input.required<string>();
    public ariaLabelledBy: InputSignal<string | null> = input<string | null>(null);
    protected isSelectOpen: WritableSignal<boolean> = signal(false);
    protected optionsList: Signal<Array<SelectOption>>;
    protected readonly inputValue: WritableSignal<string> = signal('');
    protected readonly selectedOption: Signal<SelectOption | undefined>;
    protected readonly optionElements = viewChildren<ElementRef>('inputElement');
    protected readonly toggleButtonElement = viewChild<ElementRef>('toggleButtonElement');
    protected readonly optionGroupElement = viewChild<ElementRef<HTMLElement>>('optionGroupElement');
    protected readonly isOpeningUpward: WritableSignal<boolean> = signal(false);
    protected isDisabled: WritableSignal<boolean> = signal(false);
    protected readonly ICONS = ICONS;
    private readonly inputService: InputService = inject(InputService);
    private readonly hostElement: ElementRef<HTMLElement> = inject(ElementRef);
    private onChange: (value: string) => void;
    private onTouched: () => void;

    public constructor() {
        this.onChange = (): void => {
        };

        this.onTouched = (): void => {
        };

        this.selectedOption = computed((): SelectOption | undefined => {
            return this.options()
                .find(option => option.value === this.inputValue());
        });

        this.optionsList = computed((): Array<SelectOption> => {
            return this.options()
                .map((option: SelectOption) => {
                    return {
                        ...option,
                        isSelected: option.value === this.selectedOption()?.value
                    };
                });
        });

        /*
         * Runs after rendering but before the browser paints, so the option group can be
         * measured and flipped above the button without any visible flickering.
         */
        afterRenderEffect({
            earlyRead: (): boolean | undefined => {
                const optionGroupElement = this.optionGroupElement();
                const toggleButtonElement = this.toggleButtonElement();

                if (!this.isSelectOpen() || !optionGroupElement || !toggleButtonElement) {
                    return undefined;
                }

                return this.hasToOpenUpward(toggleButtonElement.nativeElement, optionGroupElement.nativeElement);
            },
            write: (isOpeningUpward: Signal<boolean | undefined>): void => {
                const hasToOpenUpward = isOpeningUpward();

                if (hasToOpenUpward === undefined) {
                    return;
                }

                this.isOpeningUpward.set(hasToOpenUpward);
                untracked(() => this.setFocusOnSelectedOption());
            }
        });
    }

    public writeValue(value: string): void {
        this.inputValue.set(value ?? '');
    }

    public registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled.set(isDisabled);

        if (isDisabled) {
            this.isSelectOpen.set(false);
        }
    }

    protected toggleOptions(): void {
        if (this.isSelectOpen()) {
            this.isSelectOpen.set(false);
            this.setFocusOnToggleButton();
        } else {
            this.isSelectOpen.set(true);
        }

        this.onTouched();
    }

    /*
     * The checked state of the radio input is not evaluated, because the first option is focused but not
     * checked when no value is selected yet. Arrow key navigation fires click events with detail 0, which
     * are ignored by isClickEvent, so only an explicit click, Enter or Space selects an option.
     */
    protected selectOption(optionValue: string, selectEvent: KeyboardEvent | PointerEvent): void {
        const isEscapeKeyPressed = this.inputService.isEscapeKeyPressed(selectEvent as KeyboardEvent);
        const isEnterKeyPressed = this.inputService.isEnterKeyPressed(selectEvent as KeyboardEvent);
        const isSpaceKeyPressed = this.inputService.isSpaceKeyPressed(selectEvent as KeyboardEvent);
        const isClickEvent = this.inputService.isClickEvent(selectEvent as PointerEvent);

        if (isEscapeKeyPressed) {
            selectEvent.preventDefault();
            selectEvent.stopPropagation();
            this.toggleOptions();

            return;
        }

        if (isEnterKeyPressed || isSpaceKeyPressed || isClickEvent) {
            if (isEnterKeyPressed) {
                selectEvent.preventDefault();
            }

            this.setSelectedOptionByValue(optionValue);
            this.toggleOptions();
        }
    }

    protected onHostElementFocusOut(focusOutEvent: FocusEvent): void {
        const nextFocusedElement = focusOutEvent.relatedTarget as Node | null;

        if (nextFocusedElement && !this.isInsideHostElement(nextFocusedElement) && !nextFocusedElement.contains(this.hostElement.nativeElement)) {
            this.isSelectOpen.set(false);
            this.onTouched();
        }
    }

    /*
     * Closing the options requires two listeners, because 'focusout' alone is not reliable:
     *
     * Safari and Firefox on macOS do not move focus to buttons or radio buttons when
     * they are clicked. When clicking an option, 'focusout' is therefore fired with
     * 'relatedTarget' set to null, exactly like a click on a non-focusable area outside
     * the component. Closing the options whenever 'relatedTarget' is null would remove
     * the options from the DOM before the click on them is handled.
     *
     * onHostElementFocusOut only closes the options if focus moves to a known element outside
     * the host element (e.g. via Tab / Shift+Tab or a click on another focusable element).
     *
     * onDocumentPointerDown covers the null case by checking where the pointer was actually
     * pressed, which works independently of the browser's focus behaviour.
     */
    protected onDocumentPointerDown(pointerEvent: PointerEvent): void {
        if (this.isSelectOpen() && !this.isInsideHostElement(pointerEvent.target as Node)) {
            this.isSelectOpen.set(false);
            this.onTouched();
        }
    }

    private setSelectedOptionByValue(optionValue: string): void {
        const selectedOption = this.options()
            .find(option => option.value === optionValue);

        if (selectedOption) {
            this.inputValue.set(selectedOption.value);
            this.onChange(selectedOption.value);
            this.onTouched();
        }
    }

    private hasToOpenUpward(toggleButtonElement: HTMLElement, optionGroupElement: HTMLElement): boolean {
        const toggleButtonRect = toggleButtonElement.getBoundingClientRect();
        const optionGroupHeight = optionGroupElement.getBoundingClientRect().height;
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
        const spaceBelow = viewportHeight - toggleButtonRect.bottom;
        const spaceAbove = toggleButtonRect.top;

        return optionGroupHeight > spaceBelow && spaceAbove > spaceBelow;
    }

    private setFocusOnSelectedOption(): void {
        const optionElements = this.optionElements();
        const selectedOptionValue = this.selectedOption()?.value;
        const selectedOptionElement = optionElements.find(optionElement => {
            return optionElement.nativeElement.value === selectedOptionValue;
        }) ?? optionElements.at(0);

        selectedOptionElement
            ?.nativeElement
            ?.focus({ preventScroll: true });

        if (selectedOptionElement) {
            this.scrollOptionIntoView(selectedOptionElement.nativeElement);
        }
    }

    private scrollOptionIntoView(optionInputElement: HTMLElement): void {
        const optionGroupElement = this.optionGroupElement()?.nativeElement;
        const optionLabelElement = optionInputElement.closest<HTMLElement>('[for="' + optionInputElement.id + '"]');

        if (!optionGroupElement || !optionLabelElement) {
            return;
        }

        const optionTop = optionLabelElement.offsetTop;
        const optionBottom = optionTop + optionLabelElement.offsetHeight;
        const visibleBottom = optionGroupElement.scrollTop + optionGroupElement.clientHeight;

        if (optionTop < optionGroupElement.scrollTop) {
            optionGroupElement.scrollTop = optionTop;
        } else if (optionBottom > visibleBottom) {
            optionGroupElement.scrollTop = optionBottom - optionGroupElement.clientHeight;
        }
    }

    private setFocusOnToggleButton(): void {
        this.toggleButtonElement()
            ?.nativeElement
            ?.focus();
    }

    private isInsideHostElement(node: Node | null): boolean {
        return !!node && this.hostElement.nativeElement.contains(node);
    }
}
